import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import { DesignFirmType, TeamsInterface } from 'ts-interfaces';

// In-memory cache (module-scoped)
const firmCache = new Map(); // key: id, value: firmData
const subscriptionCache = new Map(); // key: subscriptionId, value: subscriptionData

export const useDesignFirm = (db, id, subscriptionURL) => {
  const [firm, setFirm] = useState<DesignFirmType | null>(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);


  const deactivateTeamMember = useCallback(async (member: TeamsInterface) => {
    if (!db || !id) {
      console.error('Cannot deactivate team member: missing db or design firm ID');
      return { success: false, error: 'Missing db or design firm ID' };
    }
    try {
      const docRef = doc(db, 'teams', member.id);
      await updateDoc(docRef, { status: 'inactive' });
      return { success: true };
    } catch (err) {
      console.error('Error deactivating team member:', err);
      return { success: false, error: err };
    }
  });

  useEffect(() => {
    if (!db || !id) return;

    let isCancelled = false;

    const fetchFirmAndSubscription = async () => {
      setLoading(true);
      setError(null);

      try {
        // Check firm cache first
        let firmData:DesignFirmType = firmCache.get(id);

        if (!firmData) {
          const docRef = doc(db, 'design_firms', id);
          const docSnap = await getDoc(docRef);

          if (!docSnap.exists()) {
            if (!isCancelled) {
              setFirm(null);
              setSubscription(null);
            }
            firmCache.delete(id); // just in case
            setLoading(false);
            return;
          }

          firmData = { id: docSnap.id, ...docSnap.data() } as DesignFirmType;
          firmCache.set(id, firmData);
        }

        if (!isCancelled) {
          setFirm(firmData);
        }

        // Check subscription cache if subscriptionId exists
        if (firmData.stripeSubscriptionId && subscriptionURL) {
          const cachedSub = subscriptionCache.get(firmData.stripeSubscriptionId);
          if (cachedSub) {
            if (!isCancelled) setSubscription(cachedSub);
          } else {
            try {
              const response = await axios.post(subscriptionURL, {
                subscriptionId: firmData.stripeSubscriptionId,
              });
              subscriptionCache.set(firmData.stripeSubscriptionId, response.data);
              if (!isCancelled) setSubscription(response.data);
            } catch (subscriptionError) {
                console.error('Subscription API Error Details:', {
                  message: subscriptionError.message,
                  status: subscriptionError.response?.status,
                  data: subscriptionError.response?.data,
                  url: subscriptionURL,
                  subscriptionId: firmData.stripeSubscriptionId
                });
              if (!isCancelled) setSubscription(null);
            }
          }
        } else {
          if (!isCancelled) setSubscription(null);
        }
      } catch (err) {
        console.error('Error in useDesignFirm:', err);
        if (!isCancelled) {
          setError(err);
          setFirm(null);
          setSubscription(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchFirmAndSubscription();

    return () => {
      isCancelled = true;
    };
  }, [db, id, subscriptionURL]);

  // New function to save settings to backend
  const saveSettings = useCallback(async (settings) => {
    if (!db || !id) {
      console.error('Cannot save settings: missing db or design firm ID');
      return { success: false, error: 'Missing db or design firm ID' };
    }

    setSavingSettings(true);
    console.log(`Saving settings for design firm ${id}:`, settings);

    try {
      // Update Firestore document
      const docRef = doc(db, 'design_firms', id);
      await updateDoc(docRef, { ...settings });

      // Update local cache
      if (firmCache.has(id)) {
        const cachedFirm = firmCache.get(id);
        firmCache.set(id, { ...cachedFirm, ...settings });
      }

      // Update state
      setFirm(prevFirm => {
        if (!prevFirm) return null;
        return { ...prevFirm, ...settings };
      });

      setSavingSettings(false);
      return { success: true };
    } catch (err) {
      console.error('Error saving settings:', err);
      setSavingSettings(false);
      setError(err);
      return { success: false, error: err };
    }
  }, [db, id]);

  return { firm, subscription, loading, error, saveSettings, savingSettings, deactivateTeamMember };
};

export default useDesignFirm;
